import { queryKeys } from '../../../../../../queries/queryKeys';
import { getRequestAddresses } from '../../../../../../queries/useRequestAddresses';

const excludedRoomIds = [0, 1, 2];

const getFilteredRooms = (rooms = []) =>
  rooms.filter((room) => !excludedRoomIds.includes(room.typeId));

const pushBindToHouses = (housesMap, bind) => {
  const key = bind.house.streetName;

  if (!housesMap[key]) {
    housesMap[key] = {
      streetName: key,
      houses: [],
    };
  }

  housesMap[key].houses.push(bind);
};

const pushBindToRooms = (roomsMap, bind) => {
  const { streetName, building, corpus } = bind.address;
  const houseKey = [building, corpus].filter(Boolean).join('-');

  if (!roomsMap[streetName]) {
    roomsMap[streetName] = {
      streetName,
      houses: {},
    };
  }

  if (!roomsMap[streetName].houses[houseKey]) {
    roomsMap[streetName].houses[houseKey] = {
      houseId: bind.address.houseId,
      house: building,
      corpus,
      rooms: [],
    };
  }

  roomsMap[streetName].houses[houseKey].rooms.push(bind);
};

const setCompanyBind = (map, bind) => {
  map.set(bind.serviceCompany.id, new Map());
};

const setHouseBind = (map, bind) => {
  const houses = map.get(bind.serviceCompany.id) || new Map();
  houses.set(bind.house.id, new Set());
  map.set(bind.serviceCompany.id, houses);
};

const setRoomBind = (map, bind) => {
  const houses = map.get(bind.serviceCompany.id) || new Map();
  const rooms = houses.get(bind.address.houseId) || new Set();
  houses.set(bind.address.houseId, rooms.add(bind.address.id));
  map.set(bind.serviceCompany.id, houses);
};

const createBindHouseKey = (serviceCompanyId, houseId) =>
  `${serviceCompanyId}-${houseId}`;

export const normalizeStateMap = async ({ stateMap, queryClient }) => {
  const normalizedStateMap = new Map();

  await Promise.all(
    Array.from(stateMap.entries()).map(async ([companyId, housesMap]) => {
      if (housesMap.size === 0) {
        normalizedStateMap.set(companyId, new Map());
        return;
      }

      const normalizedHousesMap = new Map();

      await Promise.all(
        Array.from(housesMap.entries()).map(async ([houseId, roomsSet]) => {
          if (roomsSet.size === 0) {
            normalizedHousesMap.set(houseId, new Set());
            return;
          }

          const rooms = await queryClient.fetchQuery(
            queryKeys.requestAddresses(houseId),
            () => getRequestAddresses(houseId)
          );
          const filteredRooms = getFilteredRooms(rooms);
          const isAllRoomsSelected =
            filteredRooms.length > 0 &&
            filteredRooms.length === roomsSet.size &&
            filteredRooms.every((room) => roomsSet.has(room.id));

          normalizedHousesMap.set(
            houseId,
            isAllRoomsSelected ? new Set() : new Set(roomsSet)
          );
        })
      );

      normalizedStateMap.set(companyId, normalizedHousesMap);
    })
  );

  return normalizedStateMap;
};

export const transformBinds = (binds) => {
  const companies = [];
  const housesMap = {};
  const roomsMap = {};
  const stateMap = new Map();
  const fullyBoundCompanies = new Set();
  const prevBinds = {
    companies: new Set(),
    houses: new Set(),
    actualHouses: new Set(),
    addresses: new Set(),
    boundHouses: new Set(),
  };

  if (binds?.length > 0) {
    binds.forEach((bind) => {
      if (!bind.house && !bind.address) {
        fullyBoundCompanies.add(bind.serviceCompany.id);
      }
    });

    const addressBindHouseKeys = binds.reduce((acc, bind) => {
      if (bind.address) {
        acc.add(
          createBindHouseKey(bind.serviceCompany.id, bind.address.houseId)
        );
      }

      return acc;
    }, new Set());

    binds.forEach((bind) => {
      if (bind.address) {
        if (!fullyBoundCompanies.has(bind.serviceCompany.id)) {
          setRoomBind(stateMap, bind);
        }

        pushBindToRooms(roomsMap, bind);
        prevBinds.addresses.add(bind.address.id);
        prevBinds.boundHouses.add(bind.address.houseId);
        return;
      }

      if (bind.house) {
        prevBinds.actualHouses.add(bind.house.id);

        if (
          !fullyBoundCompanies.has(bind.serviceCompany.id) &&
          !addressBindHouseKeys.has(
            createBindHouseKey(bind.serviceCompany.id, bind.house.id)
          )
        ) {
          setHouseBind(stateMap, bind);
          prevBinds.houses.add(bind.house.id);
        }

        prevBinds.boundHouses.add(bind.house.id);
        pushBindToHouses(housesMap, bind);
        return;
      }

      setCompanyBind(stateMap, bind);
      companies.push(bind);
      prevBinds.companies.add(bind.serviceCompany.id);
    });
  }

  return {
    companyBinds: companies,
    houseBinds: Object.values(housesMap),
    roomBinds: Object.values(roomsMap).map(({ streetName, houses }) => ({
      streetName,
      houses: Object.values(houses),
    })),
    stateMap,
    prevBinds,
  };
};
